"use client"

import { useState, type ReactNode } from "react"
import {
  HerbicideTimingResult,
  parseWindowStartDate,
  parseWindowEndDate,
} from "@/lib/herbicide-timing-calculator"
import { Button } from "@/components/ui/button"
import { ProductRecommendations } from "@/components/tools/product-recommendations"
import {
  Calendar,
  MapPin,
  Package,
  Sprout,
  Plus,
  ArrowRight,
  AlertTriangle,
} from "lucide-react"
import { downloadICSFile, CalendarEvent } from "@/lib/calendar-utils"
import { HerbicideProduct } from "@/data/herbicide-products"
import { getProductByAsin, titleIsMeaningfullyDifferent } from "@/lib/affiliate-card-lookup"
import { resolveShortlinkAsin } from "@/lib/affiliate-shortlink-aliases"
import { contentAnalytics } from "@/lib/analytics"
import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"
import Image from "next/image"
import Link from "next/link"
import {
  ApproachCard,
  CalendarWindow,
  EdgeCard,
  HeadlineHero,
  UnifiedShareButton,
  type EdgeCardStat,
} from "@/components/tools/shared"
import { YearCalendar } from "@/components/tools/shared/year-calendar"
import { buildHerbicideCalendar } from "@/lib/herbicide-month-grid"
import { herbicideTimingFaqs } from "@/data/tool-faqs/herbicide-timing"
import { buildToolUrl } from "@/lib/result-tool-urls"

interface HerbicideTimingResultsProps {
  result: HerbicideTimingResult
  /** True when the form auto-filled weeds because the user didn't pick any.
   *  Surfaces a "Based on X, Y, Z — change weeds" disclosure under the hero so
   *  the result still feels personalized. View-only flag — not stored on the
   *  calculator result object. */
  weedsWereAutoPicked?: boolean
}

type EdgeKind = "too-early" | "open-now" | "missed" | "closing" | null

function classifyEdge(result: HerbicideTimingResult): EdgeKind {
  // Edge cards are pre-emergent-centric (open-now, closing, missed, too-early
  // all describe a pre-emergent window). Only surface them when the engine
  // has actually decided pre-emergent should lead — otherwise the post-
  // emergent path uses the standard hero with no "too early" framing.
  if (result.recommendedApproach !== "pre-emergent") return null
  const t = result.preEmergentTiming
  if (!t) return null
  if (t.urgencyLevel === "too-late") return "missed"
  if (t.urgencyLevel === "critical") return "open-now"
  if (t.urgencyLevel === "late") return "closing"
  if (t.urgencyLevel === "optimal" && t.daysRemaining > 60) return "too-early"
  return null
}

function formatMonthDay(d: Date) {
  // timeZone: "UTC" pins the formatted day so the calculator engine's UTC dates
  // render the same label regardless of the viewer's timezone (avoids an
  // off-by-one day near midnight and the React #418 TZ-trap class).
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })
}

// Wrap "pre-emergent" and "post-emergent" in non-breaking spans so the
// browser doesn't split them across lines at the hyphen. Used for any
// dynamic copy from the calculator (currentStatus, nextAction, status)
// since we can't edit those strings via JSX directly.
function nbHyphenate(text: string): ReactNode {
  const parts = text.split(/(\bpre-emergent\b|\bpost-emergent\b)/gi)
  return parts.map((part, i) =>
    /^(pre|post)-emergent$/i.test(part) ? (
      <span key={i} className="whitespace-nowrap">{part}</span>
    ) : (
      part
    ),
  )
}

export function HerbicideTimingResults({ result, weedsWereAutoPicked = false }: HerbicideTimingResultsProps) {
  if (!result || !result.grassType || !result.region) {
    return null
  }

  const calendarEvent = createHerbicideCalendarEvent(result)
  const edge = classifyEdge(result)
  const preLeads = result.recommendedApproach === "pre-emergent"
  const postLeads = result.recommendedApproach === "post-emergent"
  // View-layer override: when pre-emergent's window has been missed, treat
  // post-emergent as the user's actionable path even though the engine keeps
  // `recommendedApproach = "pre-emergent"` for explicit-pre users on purpose
  // (so the EdgeCard's "you missed pre" educational copy still leads).
  // Drives product order, mobile card stacking order, and the section heading.
  const postShouldLead = postLeads || edge === "missed"
  // Post-emergent can lead even when conditions are dormant (e.g., pre is
  // too-late this season AND we're in winter). The hero needs different copy
  // in that case — "Treat what's growing now" is wrong when nothing is growing.
  const postCond = result.postEmergentTiming?.currentConditions.toLowerCase() ?? ""
  const postDormant = postCond.includes("dormant") || postCond.includes("poor")
  const postLeadsDormant = postLeads && postDormant

  const windowStr = result.preEmergentTiming?.optimalWindow ?? ""
  const windowStart = windowStr ? parseWindowStartDate(windowStr) : null
  const windowEnd = windowStr ? parseWindowEndDate(windowStr) : null

  // True when the calculated window opens more than two weeks in the future.
  // The body content of this page lists a single "primary" pre-emergent
  // window per region, but the calculator personalizes the window per
  // targeted weed and grass type, so the user can see (e.g.) a May–June
  // window for goosegrass on a Southeast bermuda lawn while the static
  // table lists Feb–Mar for crabgrass. Without this hint the two appear to
  // contradict each other. Only relevant when pre-emergent is leading.
  const isUpcomingWindow =
    preLeads &&
    !edge &&
    windowStart !== null &&
    windowStart.getTime() > Date.now() + 14 * 24 * 60 * 60 * 1000

  // Calendar renders whenever pre-emergent leads and we have a valid window —
  // including edge states. Visualizing the window is the most direct answer to
  // "when?", and the EdgeCard below adds the "where am I relative to it?" +
  // urgency framing. They reinforce each other instead of competing.
  const showCalendarGrid =
    preLeads && windowStart && windowEnd && windowStart.getTime() <= windowEnd.getTime()

  // When the window is still ahead of us, lead with a single "apply on" date
  // (window start) so the page delivers on the hero's "down to the day" promise.
  // When we're already inside the window, fall back to the range copy so we
  // don't anchor the user to a date that's already passed.
  const headlineDate =
    windowStart && windowEnd && windowStart.getTime() > Date.now()
      ? formatMonthDay(windowStart)
      : null

  const headlineDates =
    windowStart && windowEnd && !headlineDate
      ? `${formatMonthDay(windowStart)} and ${formatMonthDay(windowEnd)}`
      : null

  // Amber accent communicates urgency. Only show it when pre-emergent is
  // leading with a near-term/closing window — for post-led results the user
  // isn't on a deadline, so green (default) is correct.
  const accentTone: "green" | "amber" =
    preLeads &&
    (result.preEmergentTiming?.urgencyLevel === "critical" ||
      result.preEmergentTiming?.urgencyLevel === "late")
      ? "amber"
      : "green"

  // Featured product matches the leading approach so the user's first product
  // suggestion is for the action they should take now. When post leads (or
  // pre is missed), surface post first; otherwise default to pre.
  const preProducts = result.recommendedProducts.preEmergent
  const postProducts = result.recommendedProducts.postEmergent
  const [primaryList, secondaryList] = postShouldLead
    ? [postProducts, preProducts]
    : [preProducts, postProducts]
  const featuredProduct = primaryList[0] ?? secondaryList[0]
  const secondaryAll = [...primaryList.slice(1), ...secondaryList.slice(0, 2)].slice(0, 4)

  // Urgent states where the user should buy/apply today. We surface the
  // featured product right next to the EdgeCard for these cases so it's
  // adjacent to the "act now" framing instead of three sections below.
  // Excludes too-early (no rush) and any state where post-emergent would
  // be dormant ("wait for active growth" isn't an act-now state).
  const isUrgent =
    edge === "open-now" ||
    edge === "closing" ||
    (postLeads && !postLeadsDormant) ||
    (edge === "missed" && !postLeadsDormant)

  const herbicideCalendar = buildHerbicideCalendar(result)

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${buildToolUrl('herbicide-timing', {
          zip: result.zipCode,
          grassType: result.grassType,
          applicationType: result.applicationType,
          targetWeeds: result.targetWeeds,
        })}`
      : ""

  return (
    <div className="space-y-10" id="results-content">
      {/* 1. Result header */}
      <div>
        <div className="inline-flex items-center gap-2 rounded-full bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700">
          <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
          {result.region} · {result.grassType}
        </div>
        <HeadlineHero
          align="left"
          title={
            result.recommendedApproach === "none" ? (
              "We couldn't find timing for these inputs."
            ) : postLeadsDormant ? (
              "Wait for active growth."
            ) : postLeads ? (
              "Treat what's growing now."
            ) : edge === "missed" ? (
              <>The <span className="whitespace-nowrap">pre-emergent</span> window has passed.</>
            ) : edge === "open-now" ? (
              "Your window is open right now."
            ) : edge === "closing" ? (
              "The window just closed."
            ) : edge === "too-early" ? (
              "Hold off, you're early this season."
            ) : headlineDate ? (
              <>Apply your <span className="whitespace-nowrap">pre-emergent</span> on</>
            ) : headlineDates ? (
              <>Apply your <span className="whitespace-nowrap">pre-emergent</span> between</>
            ) : (
              <>Your herbicide timing</>
            )
          }
          accent={
            result.recommendedApproach === "none"
              ? "Try a different weed or grass combination."
              : postLeadsDormant
                ? "We'll tell you when it's time."
                : postLeads
                  ? "Apply post-emergent during active growth."
                  : edge === "missed"
                    ? "Here's what to do instead."
                    : edge === "open-now"
                      ? "Don't wait."
                      : edge === "closing"
                        ? "Late, but not over."
                        : edge === "too-early"
                          ? "We'll tell you when it's time."
                          : headlineDate ?? headlineDates ?? undefined
          }
          accentTone={accentTone}
          subtitle={nbHyphenate(result.currentStatus)}
        />
        {isUpcomingWindow && (
          <p className="mt-3 text-sm text-gray-600">
            This is the next pre-emergent window for the weeds and grass you
            selected. The regional crabgrass primary may have already passed —
            see the timing table below for region-wide context.
          </p>
        )}
        {weedsWereAutoPicked && result.targetWeeds.length > 0 && (
          <p className="mt-3 text-sm text-gray-600">
            Planning assumption: {result.targetWeeds.slice(0, 3).join(", ")} are common in {result.region}.{" "}
            <a
              href="#herbicide-timing-form"
              className="font-semibold text-green-700 underline-offset-2 hover:underline"
            >
              Pick the actual weed
            </a>
            before applying any product.
          </p>
        )}
      </div>

      <LabelSafetyNotice limitations={result.safetyLimitations} />

      {/* 2. Calendar OR EdgeCard */}
      {showCalendarGrid && windowStart && windowEnd && (
        <CalendarWindow
          windowStart={windowStart}
          windowEnd={windowEnd}
          rightStat={
            result.preEmergentTiming ? (
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                  Soil temp target
                </div>
                <div className="mt-1 text-3xl font-extrabold tabular-nums tracking-tight text-gray-900">
                  {result.preEmergentTiming.soilTempRequirement}
                </div>
                <div className="mt-0.5 text-xs text-green-700">trigger temperature</div>
              </div>
            ) : null
          }
        />
      )}

      {edge && (
        <EdgeCard
          tone={
            edge === "open-now" || edge === "closing"
              ? "amber"
              : edge === "too-early"
                ? "ink"
                : "muted"
          }
          tag={
            edge === "open-now"
              ? "Open now"
              : edge === "closing"
                ? "Just closed"
                : edge === "too-early"
                  ? "Too early"
                  : "Window closed"
          }
          tagSub={
            edge === "open-now"
              ? "You're inside the window"
              : edge === "closing"
                ? "Late, but you can still spot-treat"
                : edge === "too-early"
                  ? "Window opens later this year"
                  : "Pre-emergent won't help this season"
          }
          headline={nbHyphenate(result.currentStatus)}
          body={nbHyphenate(result.nextAction)}
          stats={buildEdgeStats(result, edge)}
          primary={
            edge === "open-now" && calendarEvent
              ? {
                  label: "Add to calendar",
                  onClick: () =>
                    downloadICSFile(
                      [calendarEvent],
                      `pre-emergent-${result.grassType.replace(/\s+/g, "-").toLowerCase()}.ics`
                    ),
                }
              : edge === "missed"
                ? { label: "See post-emergent picks", href: "#products-section" }
                : edge === "closing"
                  ? { label: "Spot-treat what's already up", href: "#products-section" }
                  : undefined
          }
          secondary={
            // For open-now we still want a "Browse products" escape hatch
            // alongside the calendar CTA. For missed/closing the primary now
            // points at products, so a secondary would be redundant.
            edge === "open-now" && calendarEvent
              ? { label: "Browse products", href: "#products-section" }
              : edge === "too-early"
                ? { label: "Browse products", href: "#products-section" }
                : undefined
          }
        />
      )}

      {/* Urgent inline featured product — sits adjacent to the act-now CTA
          so the user doesn't have to scroll past two ApproachCards to buy. */}
      {isUrgent && featuredProduct && (
        <div>
          <FeaturedProductCard
            product={featuredProduct}
            targetWeeds={result.targetWeeds}
            grassType={result.grassType}
            urgent
          />
          <div className="mt-2 text-center text-xs text-gray-500">
            <Link
              href="#products-section"
              className="font-semibold text-green-700 hover:underline"
            >
              See all options safe for {result.grassType}
            </Link>
          </div>
        </div>
      )}

      {preLeads && !showCalendarGrid && !edge && windowStr && (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 text-sm text-gray-700">
          <div className="text-[11px] font-bold uppercase tracking-wider text-green-700">
            Window
          </div>
          <div className="mt-1 text-2xl font-extrabold tracking-tight text-gray-900">
            {windowStr}
          </div>
          <p className="mt-2 text-gray-500">{result.preEmergentTiming?.status}</p>
        </div>
      )}

      {/* 2b. Year-at-a-glance — full 12-month context, paired with the
          focused window visualization above. */}
      {herbicideCalendar && (
        <section className="rounded-2xl border border-gray-200 bg-white p-6">
          <div className="mb-3 text-[11px] font-bold uppercase tracking-wider text-gray-500">
            Year at a glance
          </div>
          <YearCalendar
            variant="full"
            months={herbicideCalendar.cells}
            bestMonth={herbicideCalendar.bestMonth}
            ringColors={{ today: "#374151", best: "#15803d" }}
            todayDot
            ariaLabel="12-month herbicide application calendar"
          />
          <div className="mt-3 flex flex-wrap items-center gap-4 text-[11.5px] text-gray-500">
            {herbicideCalendar.hasPre && (
              <span className="inline-flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-sm bg-green-600" /> Pre-emergent window
              </span>
            )}
            {herbicideCalendar.hasPost && (
              <span className="inline-flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-sm bg-green-100" /> Post-emergent window
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-gray-900" /> Today
            </span>
          </div>
        </section>
      )}

      {/* 3. Two ApproachCards — when post should lead, render post first so
          mobile (single column) puts the "Start here" card above the planning
          card. Desktop side-by-side is symmetric so order doesn't matter. */}
      {(result.preEmergentTiming || result.postEmergentTiming) && (() => {
        const preCard = result.preEmergentTiming && (
          <ApproachCard
            key="pre"
            kind="pre"
            tag="Pre-emergent"
            tagSub={
              // "Focus on now" is wrong when pre is closed for the season
              // (edge="missed") or months out (edge="too-early"). It's also
              // wrong when post leads — pre is just informational then.
              preLeads && edge !== "missed" && edge !== "too-early"
                ? "What you should focus on now"
                : "Plan for the next window"
            }
            title="Stop the next generation"
            body={nbHyphenate(result.preEmergentTiming.status)}
            bullets={result.applicationTips.slice(0, 3)}
            cta={{ label: "See pre-emergents safe for your grass", href: "#products-section" }}
            // When pre's window has passed, don't slap "Start here" on it —
            // the EdgeCard is telling the user to switch to post, so post
            // should hold the visual highlight.
            recommended={preLeads && edge !== "missed"}
          />
        )
        const postCard = result.postEmergentTiming && (
          <ApproachCard
            key="post"
            kind="post"
            tag="Post-emergent"
            tagSub={
              postLeads || edge === "missed"
                ? "What you should focus on now"
                : "If you've already got visible weeds"
            }
            title="Knock down what's already up"
            body={nbHyphenate(result.postEmergentTiming.status)}
            bullets={result.postEmergentTiming.weatherRequirements.slice(0, 3)}
            cta={{ label: "See post-emergent picks", href: "#products-section" }}
            // When pre's window has passed, the EdgeCard sends the user to
            // post, so post should hold the "Start here" badge even though
            // recommendedApproach is still nominally "pre-emergent".
            recommended={postLeads || edge === "missed"}
          />
        )
        const cards = postShouldLead ? [postCard, preCard] : [preCard, postCard]
        return (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {cards}
          </div>
        )
      })()}

      {/* 4. Secondary actions */}
      <div className="flex flex-wrap items-center justify-center gap-3">
        {calendarEvent && edge !== "missed" && edge !== "closing" && (
          <Button
            type="button"
            onClick={() =>
              downloadICSFile(
                [calendarEvent],
                `pre-emergent-${result.grassType.replace(/\s+/g, "-").toLowerCase()}.ics`
              )
            }
            className="min-h-[44px] rounded-xl bg-green-600 px-5 text-sm font-bold text-white shadow-md shadow-green-200 hover:bg-green-700"
          >
            <Calendar className="mr-2 h-4 w-4" aria-hidden="true" />
            Add to calendar
          </Button>
        )}
        <UnifiedShareButton
          url={shareUrl}
          title="Herbicide Timing Results"
          trackingId="herbicide-timing"
          text={`Herbicide timing for ${result.grassType}`}
          variant="outline"
          className="min-h-[44px]"
        />
      </div>

      {/* 5. Products — featured + secondary (featured suppressed when
          already shown inline next to the urgent CTA above) */}
      <section id="products-section" className="space-y-5">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">
            <Package className="mr-2 inline h-5 w-5 text-green-600" aria-hidden="true" />
            {(isUrgent || edge === "missed") && featuredProduct
              ? `More options safe for ${result.grassType}`
              : `Products safe for ${result.grassType}`}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Matched to your target weeds. Affiliate links, your price doesn't change.
          </p>
        </div>
        {featuredProduct && !isUrgent ? (
          <FeaturedProductCard
            product={featuredProduct}
            targetWeeds={result.targetWeeds}
            grassType={result.grassType}
          />
        ) : !featuredProduct ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
            We couldn't find specific herbicide products rated for {result.grassType} and your target
            weeds. Check with your local cooperative extension for {result.region}-specific
            recommendations.
          </div>
        ) : null}
        {secondaryAll.length > 0 && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {secondaryAll.map((product) => (
              <CompactProductCard key={product.name} product={product} />
            ))}
          </div>
        )}
        <div className="pt-2 text-center">
          <Link
            href="/products?category=Herbicides+%26+Weed+Control&source=herbicide-timing"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-700 hover:text-green-800"
          >
            Browse all herbicide products <Sprout className="h-3.5 w-3.5" />
          </Link>
        </div>
      </section>

      {/* 6. FAQ + related grid */}
      <div className="grid grid-cols-1 gap-12 md:grid-cols-2">
        <FaqAccordion />
        <RelatedRail />
      </div>

      {/* 6b. How we calculated this — methodology disclosure for trust + dwell */}
      <section className="rounded-2xl border border-gray-200 bg-white p-6">
        <div className="mb-3 text-[11px] font-bold uppercase tracking-wider text-gray-500">
          How we calculated this
        </div>
        <h2 className="mb-4 text-xl font-bold tracking-tight text-gray-900">
          Where your date comes from
        </h2>
        <dl className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wider text-green-700">
              Region
            </dt>
            <dd className="mt-1 text-sm text-gray-700">
              <span className="font-semibold text-gray-900">{result.region}.</span>{" "}
              Soil temp curves and weed lifecycles for your climate zone drive the window.
            </dd>
          </div>
          {result.preEmergentTiming?.soilTempRequirement && (
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-green-700">
                Soil temp trigger
              </dt>
              <dd className="mt-1 text-sm text-gray-700">
                <span className="font-semibold text-gray-900">
                  {result.preEmergentTiming.soilTempRequirement}.
                </span>{" "}
                Most annual grassy weeds germinate above this 4-inch soil temperature.
              </dd>
            </div>
          )}
          {result.targetWeeds.length > 0 && (
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wider text-green-700">
                Target weeds
              </dt>
              <dd className="mt-1 text-sm text-gray-700">
                <span className="font-semibold text-gray-900">
                  {result.targetWeeds.slice(0, 3).join(", ")}.
                </span>{" "}
                The window is tuned to the earliest-germinating weed in your selection.
              </dd>
            </div>
          )}
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wider text-green-700">
              Sources
            </dt>
            <dd className="mt-1 text-sm text-gray-700">
              Regional cooperative extension data, 30-year soil temperature normals, and weed germination biology references. Methodology details live on each linked guide above.
            </dd>
          </div>
        </dl>
      </section>

      {/* 7. Cross-sell to fertilizer (no herbicide overlap with section 5).
          Keywords intentionally omitted — the API uses them to backfill
          cross-category matches, which leaks weed-control and grass-seed
          products into a section we want to stay fertilizer-only. */}
      <ProductRecommendations
        categories={["Fertilizers & Soil Health"]}
        title="What to apply alongside"
        trackingContext="herbicide-timing-calculator"
        compact
      />
    </div>
  )
}

function LabelSafetyNotice({ limitations }: { limitations?: string[] }) {
  if (!limitations?.length) return null

  return (
    <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:p-5 text-amber-950">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        </span>
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-amber-700">
            Label check required
          </div>
          <h2 className="mt-1 text-lg font-bold tracking-tight text-amber-950">
            Use this as timing guidance, not permission to apply.
          </h2>
          <ul className="mt-3 space-y-2 text-sm leading-relaxed">
            {limitations.slice(0, 4).map((item) => (
              <li key={item} className="flex gap-2">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-600" aria-hidden />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}

function buildEdgeStats(result: HerbicideTimingResult, edge: Exclude<EdgeKind, null>): EdgeCardStat[] {
  const t = result.preEmergentTiming
  if (!t) return []
  if (edge === "too-early") {
    return [
      { eyebrow: "Target soil", big: t.soilTempRequirement, sub: "pre-emergent threshold" },
      {
        eyebrow: "Best guess",
        big: t.daysRemaining > 0 ? `${t.daysRemaining}d` : "—",
        sub: "until window opens",
      },
      { eyebrow: "Region", big: result.region, sub: "based on your ZIP", tone: "green" },
    ]
  }
  if (edge === "open-now") {
    return [
      {
        eyebrow: "Closes in",
        big: `${Math.max(0, t.daysRemaining)}d`,
        sub: "every day past cuts effectiveness",
        tone: "amber",
      },
      { eyebrow: "Soil target", big: t.soilTempRequirement, sub: "you're past the trigger", tone: "green" },
      { eyebrow: "Region", big: result.region, sub: "your ZIP" },
    ]
  }
  if (edge === "closing") {
    return [
      {
        eyebrow: "Closed",
        big: `${Math.abs(t.daysRemaining)}d ago`,
        sub: "effectiveness drops fast",
        tone: "amber",
      },
      { eyebrow: "Soil target", big: t.soilTempRequirement, sub: "you're past it" },
      { eyebrow: "Region", big: result.region, sub: "your ZIP" },
    ]
  }
  return [
    { eyebrow: "Pre-emergent", big: "Closed", sub: "reopens late summer for fall weeds" },
    { eyebrow: "Post-emergent", big: "Open", sub: "spot-treat visible weeds", tone: "green" },
    { eyebrow: "Region", big: result.region, sub: "your ZIP" },
  ]
}

function FaqAccordion() {
  const [open, setOpen] = useState(0)
  const faqs = herbicideTimingFaqs.slice(0, 6)
  return (
    <div>
      <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-green-700">FAQ</div>
      <h2 className="mb-4 text-2xl font-bold tracking-tight text-gray-900">
        Quick answers, no fluff.
      </h2>
      <div className="border-t border-gray-200">
        {faqs.map((f, i) => {
          const isOpen = open === i
          return (
            <div key={f.question} className="border-b border-gray-200">
              <button
                type="button"
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="flex w-full items-center justify-between gap-3 py-4 text-left"
              >
                <span className="text-sm font-semibold text-gray-900 sm:text-base">{f.question}</span>
                <span
                  className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-all ${
                    isOpen ? "rotate-45 bg-green-600 text-white" : "bg-gray-100 text-gray-500"
                  }`}
                  aria-hidden="true"
                >
                  <Plus className="h-3.5 w-3.5" />
                </span>
              </button>
              {isOpen && (
                <p className="pb-4 text-sm leading-relaxed text-gray-500">{f.answer}</p>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function RelatedRail() {
  const items = [
    {
      badge: "GUIDE",
      title: "The full pre-emergent timing playbook",
      meta: "8 min read",
      href: "/blog/granular-pre-emergent-herbicide-timing-application-guide",
    },
    {
      badge: "TOOL",
      title: "Crabgrass identification",
      meta: "Photo-based",
      // `/identify` 301s to `/` — link the canonical identify page directly so
      // the click doesn't spend a redirect and land somewhere unrelated.
      href: "/what-kind-of-grass-do-i-have",
    },
    {
      badge: "TOOL",
      title: "Seeding calculator",
      meta: "Plan your reseed",
      href: "/tools/seeding-calculator",
    },
    {
      badge: "TOOL",
      title: "Fertilizer schedule",
      meta: "Coordinate with herbicide",
      href: "/tools/fertilizer-calculator",
    },
    {
      badge: "GUIDE",
      title: "Lawn Weed Control Guide",
      meta: "Pillar guide",
      href: "/guides/weed-control",
    },
  ]
  return (
    <div>
      <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-green-700">
        Keep reading
      </div>
      <h3 className="mb-4 text-xl font-bold tracking-tight text-gray-900">Related guides</h3>
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        {items.map((item, i) => (
          <Link
            key={item.href}
            href={item.href}
            className={`flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50 ${
              i < items.length - 1 ? "border-b border-gray-100" : ""
            }`}
          >
            <span className="shrink-0 rounded bg-green-50 px-1.5 py-0.5 text-[10px] font-bold tracking-wider text-green-700">
              {item.badge}
            </span>
            <div className="min-w-0 flex-1">
              <div className="line-clamp-2 text-sm font-semibold leading-snug text-gray-900">{item.title}</div>
              <div className="text-xs text-gray-500">{item.meta}</div>
            </div>
            <ArrowRight className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
          </Link>
        ))}
      </div>
    </div>
  )
}

function FeaturedProductCard({
  product,
  targetWeeds,
  grassType,
  urgent = false,
}: {
  product: HerbicideProduct
  targetWeeds: string[]
  grassType: string
  urgent?: boolean
}) {
  const enrichment = resolveProductEnrichment(product.affiliateUrl)
  const Wrapper = product.affiliateUrl ? "a" : "div"
  const surface = urgent ? "tool-urgent" : "tool-section"
  // Denominator. `tool-urgent` logged clicks with no impressions; keyed on the
  // SAME surface string and product identifiers the click carries so CTR joins.
  const impressionRef = useAffiliateImpression(
    product.affiliateUrl ? surface : undefined,
    {
      article_slug: "tool:herbicide-timing",
      product_name: product.name,
      affiliate_link: product.affiliateUrl,
    },
  )
  const handleClick = () => {
    if (!product.affiliateUrl) return
    contentAnalytics.affiliateLinkClicked(
      "tool:herbicide-timing",
      product.name,
      product.affiliateUrl,
      surface
    )
    contentAnalytics.affiliateProductClicked(surface, {
      productName: product.name,
      affiliateLink: product.affiliateUrl,
      article_slug: "tool:herbicide-timing",
    })
  }
  const wrapperProps = product.affiliateUrl
    ? {
        href: product.affiliateUrl,
        target: "_blank" as const,
        rel: "noopener noreferrer nofollow sponsored",
        onClick: handleClick,
      }
    : {}
  // Urgent variant uses amber accents to match the EdgeCard's "act now"
  // tone; non-urgent (section 5) keeps the green "featured pick" treatment.
  const borderClass = urgent ? "border-amber-500" : "border-green-600"
  const shadowClass = urgent ? "shadow-amber-100" : "shadow-green-100"
  const badgeClass = urgent ? "bg-amber-500" : "bg-green-600"
  const badgeLabel = urgent ? "Apply this now" : "Featured pick"
  return (
    <div>
    <Wrapper
      ref={impressionRef}
      {...wrapperProps}
      className={`group relative block rounded-2xl border-[1.5px] ${borderClass} bg-white p-6 pt-7 shadow-lg ${shadowClass}`}
    >
      <div className={`absolute -top-2.5 left-6 rounded-full ${badgeClass} px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white shadow-sm`}>
        {badgeLabel}
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-[140px_1fr]">
        {enrichment?.imageUrl ? (
          <div className="flex h-32 w-full items-center justify-center overflow-hidden rounded-xl border border-gray-100 bg-white sm:h-32">
            <Image
              src={enrichment.imageUrl}
              alt={product.name}
              width={140}
              height={140}
              loading="lazy"
              className="h-full w-full object-contain p-2"
            />
          </div>
        ) : (
          <div className="flex h-32 items-center justify-center rounded-xl border border-gray-100 bg-gray-50 text-xs text-gray-400">
            {product.name}
          </div>
        )}
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-green-700">
            {product.type === "pre-emergent"
              ? "Pre-emergent"
              : product.type === "post-emergent"
                ? "Post-emergent"
                : "Selective"}
          </div>
          <h3 className="mt-1 text-xl font-bold tracking-tight text-gray-900 group-hover:text-green-700">
            {product.name}
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            Active: {product.activeIngredients.join(", ")} · Rate: {product.applicationRate}
          </p>
          <div className="mt-3 rounded-lg bg-green-50 p-3">
            <div className="text-[11px] font-bold uppercase tracking-wider text-green-700">
              Why we picked this
            </div>
            <p className="mt-1 text-sm text-gray-700">
              {`Effective on ${formatWeedList(targetWeeds)} and screened as compatible with ${grassType}. Confirm the exact label before applying.`}
              {product.notes[0] ? ` ${product.notes[0]}` : ""}
            </p>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-500">
            <span>~${product.costEstimate.per1000SqFt.toFixed(2)} per 1,000 sq ft</span>
            <span className="rounded-full bg-green-100 px-2 py-0.5 font-medium text-green-700">
              {product.effectiveness}
            </span>
          </div>
        </div>
      </div>
    </Wrapper>
    {/* Pet/kid safety + sizing escape hatch. Rendered as a sibling of the
        affiliate-link Wrapper so we don't nest <a> tags. Wording is
        conservative — "until dry" is generic label language, not a
        regulatory claim. */}
    <p className="mt-2 px-1 text-[11px] text-gray-500">
      Keep pets and kids off treated area until dry (typically 1–2 hrs).{" "}
      <Link
        href="/tools/lawn-size-calculator"
        className="font-semibold text-green-700 hover:underline"
      >
        Calculate how much you need
      </Link>
    </p>
    </div>
  )
}

function formatWeedList(weeds: string[]): string {
  const cleaned = weeds.filter(Boolean).slice(0, 2)
  if (cleaned.length === 0) return "your target weeds"
  if (cleaned.length === 1) return cleaned[0]
  return `${cleaned[0]} and ${cleaned[1]}`
}

function CompactProductCard({ product }: { product: HerbicideProduct }) {
  const enrichment = resolveProductEnrichment(product.affiliateUrl)
  const showAmazonTitle =
    enrichment?.amazonTitle && titleIsMeaningfullyDifferent(product.name, enrichment.amazonTitle)
  const Wrapper = product.affiliateUrl ? "a" : "div"
  const wrapperProps = product.affiliateUrl
    ? { href: product.affiliateUrl, target: "_blank" as const, rel: "noopener noreferrer nofollow sponsored" }
    : {}
  return (
    <Wrapper
      {...wrapperProps}
      className="block rounded-xl border border-gray-200 bg-white p-4 transition-colors hover:border-green-300 hover:bg-green-50/40"
    >
      <div className="flex items-start gap-3">
        {enrichment?.imageUrl && (
          <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md border border-gray-100 bg-white">
            <Image
              src={enrichment.imageUrl}
              alt={product.name}
              width={64}
              height={64}
              loading="lazy"
              className="h-full w-full object-contain p-1"
            />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h4 className="text-sm font-semibold text-gray-900">{product.name}</h4>
          </div>
          {showAmazonTitle && (
            <p className="mt-0.5 line-clamp-1 text-xs text-gray-500" title={enrichment.amazonTitle}>
              {enrichment.amazonTitle}
            </p>
          )}
          <p className="mt-1 line-clamp-2 text-xs text-gray-500">{product.notes[0] ?? product.timing}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
            <span
              className={`rounded-full px-2 py-0.5 font-medium ${
                product.effectiveness === "excellent"
                  ? "bg-green-100 text-green-700"
                  : product.effectiveness === "good"
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-gray-100 text-gray-600"
              }`}
            >
              {product.effectiveness}
            </span>
            <span className="text-gray-500">~${product.costEstimate.per1000SqFt.toFixed(2)}/1k sq ft</span>
          </div>
        </div>
      </div>
    </Wrapper>
  )
}

function createHerbicideCalendarEvent(result: HerbicideTimingResult): CalendarEvent | null {
  if (!result.preEmergentTiming?.optimalWindow) return null
  const eventDate = parseWindowStartDate(result.preEmergentTiming.optimalWindow)
  if (!eventDate) return null
  return {
    id: `herbicide-pre-emergent-${result.grassType.replace(/\s+/g, "-")}`,
    title: `Apply Pre-Emergent Herbicide (${result.grassType})`,
    description: `Time to apply pre-emergent herbicide for ${result.grassType} in ${result.region}.\n\nWindow: ${result.preEmergentTiming.optimalWindow}\nSoil temp target: ${result.preEmergentTiming.soilTempRequirement}\nTarget weeds: ${result.targetWeeds.join(", ")}`,
    dueDate: eventDate,
    reminderType: "herbicide",
  }
}

function resolveProductEnrichment(affiliateUrl?: string) {
  if (!affiliateUrl) return null
  const directMatch = affiliateUrl.match(/\/dp\/([A-Z0-9]{10})/)
  const asin = directMatch ? directMatch[1] : resolveShortlinkAsin(affiliateUrl)
  if (!asin) return null
  return getProductByAsin(asin)
}
